import React, { useState, useEffect } from 'react';
import Modal from './Modal';

interface SocietaItem {
  id: number;
  nome: string;
  coloreAccento?: string;
}

interface Props {
  /** Variante della modale: al login o al cambio */
  variante: 'login' | 'cambio';
  /** Società attualmente selezionata (prima del cambio) */
  societaCorrente: SocietaItem | null;
  /**
   * Società proposta dopo il cambio. Rilevante solo per variante="cambio".
   * null = "Tutte le società" (solo SUPERADMIN).
   */
  societaTarget?: SocietaItem | null;
  /** Lista completa delle società selezionabili */
  listaSocieta: SocietaItem[];
  /** true se l'utente è SUPERADMIN (abilita l'opzione "Tutte le società") */
  isSuperadmin: boolean;
  /** Etichette dei form attualmente dirty (da DirtyFormContext) */
  dirtyLabels: string[];
  /** Se valorizzato, il cambio è BLOCCATO. Etichetta del record in uso. */
  bloccoLabel?: string | null;
  /** Chiamato quando l'utente conferma. La società passata è quella scelta nella combo (login) o quella target (cambio). */
  onConferma: (scelta: SocietaItem | null) => void;
  /** Chiamato quando l'utente annulla (solo variante="cambio"). */
  onAnnulla?: () => void;
}

export default function ConfermaSocietaModal({
  variante,
  societaCorrente,
  societaTarget,
  listaSocieta,
  isSuperadmin,
  dirtyLabels,
  bloccoLabel,
  onConferma,
  onAnnulla,
}: Props) {
  // Nella variante "login" permettiamo il cambio contestuale della società
  const [sceltaLogin, setSceltaLogin] = useState<SocietaItem | null>(societaCorrente);

  useEffect(() => {
    if (variante === 'login') setSceltaLogin(societaCorrente);
  }, [variante, societaCorrente]);

  const isLogin = variante === 'login';
  const title = isLogin ? 'Conferma società di lavoro' : 'Cambio società';

  const renderBadge = (s: SocietaItem | null, label: string) => {
    const colore = s?.coloreAccento || 'var(--bop-color-border)';
    return (
      <div className="bop-societa-badge" style={{ borderLeftColor: colore }}>
        <div className="bop-societa-badge__label">{label}</div>
        <div className="bop-societa-badge__name">
          {s ? s.nome : 'Tutte le società'}
        </div>
      </div>
    );
  };

  const footer = isLogin ? (
    <button
      className="bop-btn-primary"
      onClick={() => onConferma(sceltaLogin)}
    >
      Continua
    </button>
  ) : (
    <>
      <button className="bop-btn-secondary" onClick={onAnnulla}>
        {bloccoLabel ? 'Chiudi' : 'Annulla'}
      </button>
      {!bloccoLabel && (
        <button
          className="bop-btn-primary"
          onClick={() => onConferma(societaTarget ?? null)}
        >
          Conferma cambio
        </button>
      )}
    </>
  );

  return (
    <Modal title={title} onClose={() => { /* non chiudibile da X */ }} footer={footer}>
      {isLogin ? (
        <div className="bop-conferma-societa">
          <p className="bop-text-muted">
            Stai per accedere a BOP con i dati della seguente società.
            Puoi cambiarla ora se non è corretta.
          </p>
          {renderBadge(sceltaLogin, 'Società in uso')}
          <div className="bop-form-group bop-mt-0">
            <label className="bop-label">Cambia società</label>
            <select
              className="bop-select"
              value={sceltaLogin ? String(sceltaLogin.id) : ''}
              onChange={e => {
                const v = e.target.value;
                if (v === '') {
                  setSceltaLogin(null);
                } else {
                  const found = listaSocieta.find(s => String(s.id) === v);
                  setSceltaLogin(found ?? null);
                }
              }}
            >
              {isSuperadmin && <option value="">Tutte le società</option>}
              {listaSocieta.map(s => (
                <option key={s.id} value={s.id}>{s.nome}</option>
              ))}
            </select>
          </div>
        </div>
      ) : (
        <div className="bop-conferma-societa">
          <p className="bop-text-muted">
            Stai per cambiare la società di lavoro.
          </p>
          <div className="bop-conferma-societa__confronto">
            {renderBadge(societaCorrente, 'Da')}
            <div className="bop-conferma-societa__freccia">→</div>
            {renderBadge(societaTarget ?? null, 'A')}
          </div>
          {bloccoLabel && (
            <div className="bop-conferma-societa__blocco">
              <strong>Non puoi cambiare società ora.</strong>
              <p>
                Stai lavorando su: <em>{bloccoLabel}</em>.
                Torna alla lista del modulo prima di cambiare società,
                altrimenti rischi di salvare modifiche su dati che non
                appartengono alla società selezionata.
              </p>
            </div>
          )}
          {!bloccoLabel && dirtyLabels.length > 0 && (
            <div className="bop-conferma-societa__warning">
              <strong>Attenzione:</strong> hai modifiche non salvate in:
              <ul className="bop-conferma-societa__dirty-list">
                {dirtyLabels.map((l, i) => <li key={i}>{l}</li>)}
              </ul>
              Se procedi, le modifiche andranno perse.
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
